import { DENOMINATIONS, countOf, denominationLabel, DENOMINATION_STYLE } from '../utils/denominations';

// modal คิดเงิน (Checkout.jsx) ใช้ธีมมืดชุดเดียวกับ CashDrawer.jsx แล้ว — ใช้ var(--surface)/
// var(--border-strong) ฯลฯ พร้อมค่าสำรอง (fallback) ทุกจุด เผื่องานธีมหลักมาประกาศตัวแปรจริง
// ใน index.css ทีหลัง (ไม่ประกาศเองในไฟล์นี้)
const stepBtn = {
  width: '36px',
  height: '40px',
  borderRadius: '8px',
  border: '1px solid var(--border-strong, #2c3140)',
  background: 'var(--surface, #171a21)',
  color: 'var(--text, #eceef3)',
  fontSize: '1.1rem',
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0
};

// รูปแบงก์/เหรียญ (swatch สี ไม่ใช่รูปถ่ายจริง — ดูเหตุผลที่ DENOMINATION_STYLE)
// เหรียญ 10 บาทจริงเป็นสองสี (วงแหวนเงิน + ตรงกลางทอง) จำลองด้วยวงกลมซ้อนกัน
// export ไว้ให้ CashDenominationTapGrid.jsx (กริดฝั่ง "รับมา" แบบแตะการ์ด) ใช้ไอคอนชุดเดียวกัน
// ไม่ต้อง copy โค้ดวาด SVG ซ้ำ
// ขนาดไอคอน 2 แบบ: sm (ค่าเริ่มต้น — ใช้ในกริดแตะรับเงิน/สเต็ปเปอร์ทอนเงินที่มีหลายช่องต่อแถว)
// กับ lg (ใช้ตอนโชว์ "หยิบทอน" ในขั้น 2 ที่มีไม่กี่ชนิด อยากให้เห็นชัดขนาดเท่าฝั่งรับเงินจริง)
const ICON_SIZES = {
  sm: { noteW: 52, noteH: 32, noteRadius: 6, coinR: 16, coinInnerR: 11, fontSmall: '11px', fontNormal: '13px' },
  lg: { noteW: 96, noteH: 48, noteRadius: 8, coinR: 23, coinInnerR: 16, fontSmall: '16px', fontNormal: '18px' },
};

export function DenominationIcon({ value, size = 'sm' }) {
  const style = DENOMINATION_STYLE[value] || { shape: 'note', color: '#6b7280', textColor: '#fff' };
  const dim = ICON_SIZES[size] || ICON_SIZES.sm;
  const fontSize = String(value).length >= 4 ? dim.fontSmall : dim.fontNormal;
  // แบงก์ 1000 พื้นน้ำตาลอ่อน ตัวเลขขาวเดี่ยวๆ อ่านยาก — ใส่ stroke เข้มรอบตัวอักษรเพิ่มคอนทราสต์
  // (จุดเดียวที่ยังอ่านยากตอนรีวิว จุดอื่นคอนทราสต์พอแล้วไม่ต้องใส่เพิ่ม)
  const needsOutline = value === 1000;

  if (style.shape === 'coin') {
    const d = dim.coinR * 2 + 2;
    const c = d / 2;
    return (
      <svg width={d} height={d} viewBox={`0 0 ${d} ${d}`} aria-hidden="true">
        {value === 10 ? (
          <>
            <circle cx={c} cy={c} r={dim.coinR} fill="#c0c4c9" stroke="#9aa1b2" strokeWidth="1" />
            <circle cx={c} cy={c} r={dim.coinInnerR} fill="#c9a227" />
          </>
        ) : (
          <circle cx={c} cy={c} r={dim.coinR} fill={style.color} stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
        )}
        <text x={c} y={c} textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fontWeight="700" fill={style.textColor}>
          {value}
        </text>
      </svg>
    );
  }

  const w = dim.noteW, h = dim.noteH;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <rect x="0.5" y="0.5" width={w - 1} height={h - 1} rx={dim.noteRadius} fill={style.color} stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
      {needsOutline && (
        <text x={w / 2} y={h / 2} textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fontWeight="700"
          fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="3" strokeLinejoin="round">
          {value}
        </text>
      )}
      <text x={w / 2} y={h / 2} textAnchor="middle" dominantBaseline="central" fontSize={fontSize} fontWeight="700" fill={style.textColor}>
        {value}
      </text>
    </svg>
  );
}

/**
 * กริดกรอกจำนวนแบงก์/เหรียญทีละชนิด — 2 บรรทัดต่อช่อง (ไอคอน+ตัวเลขบน, ปุ่ม −/จำนวน/+ ล่าง)
 * กันป้ายชื่อโดนปุ่มทับตอนคอลัมน์แคบ (เคยเป็น 1 แถวเดียว ล้นทับกันตอนใช้ในคอลัมน์ 340px ของ
 * Checkout.jsx) เรียงจากแบงก์ใหญ่ไปเหรียญเล็กในไฟล์นี้เท่านั้น (reverse ตอน render ไม่แก้ลำดับ
 * ของ DENOMINATIONS เดิม เพราะ breakdownChange()/CashDrawer.jsx's CountsForm ยังอ้างอิงลำดับเดิมอยู่)
 * counts เป็นออบเจกต์ { "20": "3", "100": "1", ... }
 * excludeValues: ค่ามูลค่าที่ไม่ต้องการให้แสดงในกริดนี้ (เช่น [1000] ตอนใช้แก้เงินทอน — ไม่แตะ
 * DENOMINATIONS เดิม กรองแค่ตอน render ในไฟล์นี้ ที่อื่นที่เรียกโดยไม่ส่ง prop นี้ยังเห็นครบ 9 ชนิด)
 */
export default function CashDenominationInput({ counts, onChange, excludeValues = [] }) {
  const setCount = (value, raw) => onChange({ ...counts, [String(value)]: raw });
  const ordered = [...DENOMINATIONS].reverse().filter(d => !excludeValues.includes(d.value));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: '0.5rem' }}>
      {ordered.map(d => {
        const n = countOf(counts, d.value);
        return (
          <div
            key={d.value}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.5rem',
              background: 'var(--surface-2, #1f232c)',
              border: '1px solid var(--border-strong, #2c3140)',
              borderRadius: '10px'
            }}
          >
            <DenominationIcon value={d.value} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <button
                type="button"
                aria-label={`${denominationLabel(d)} ลด`}
                title={denominationLabel(d)}
                onClick={() => setCount(d.value, String(Math.max(0, n - 1)))}
                style={stepBtn}
              >
                −
              </button>
              <input
                type="text"
                inputMode="numeric"
                aria-label={`จำนวน ${denominationLabel(d)}`}
                value={counts[String(d.value)] ?? ''}
                placeholder="0"
                onChange={(e) => setCount(d.value, e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ width: '36px', height: '40px', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', padding: '0 2px', border: '1px solid var(--border-strong, #2c3140)', borderRadius: '6px', boxSizing: 'border-box', background: 'var(--surface, #171a21)', color: 'var(--text, #eceef3)' }}
              />
              <button
                type="button"
                aria-label={`${denominationLabel(d)} เพิ่ม`}
                title={denominationLabel(d)}
                onClick={() => setCount(d.value, String(n + 1))}
                style={stepBtn}
              >
                +
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
